import { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";

export const SearchableSelect = ({ 
  options = [], 
  value, 
  onValueChange, 
  placeholder = "Select option...",
  searchPlaceholder = "Search...",
  loading = false,
  error = null,
  className = "",
  formatOption = (option) => option.label || option.name || option,
  getValue = (option) => option.value || option._id || option,
  emptyMessage = "No options found",
  disabled = false,
  onToggle // <-- NEW
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const filteredOptions = options.filter(option =>
    formatOption(option).toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(option => getValue(option) === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
        onToggle?.(false); // notify parent
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (option) => {
    if (disabled) return;
    onValueChange(getValue(option));
    setIsOpen(false);
    setSearchTerm("");
    onToggle?.(false);
  };

  const handleClear = (e) => {
    if (disabled) return;
    e.stopPropagation();
    onValueChange(null);
    setSearchTerm("");
  };

  const handleToggle = () => {
    if (disabled) return;
    const nextState = !isOpen;
    setIsOpen(nextState);
    onToggle?.(nextState); // notify parent
  };

  if (loading) {
    return (
      <div className={`relative ${className}`}>
        <div className="flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
          <span>Loading...</span>
          <ChevronDown className="h-4 w-4" />
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div 
        className={`flex items-center justify-between w-full px-3 py-2 border rounded-md ${
          disabled ? "bg-gray-50 cursor-not-allowed" : "cursor-pointer hover:border-gray-400"
        } ${
          error ? "border-red-300" : "border-gray-300"
        } ${isOpen && !disabled ? "ring-2 ring-igbanwo-mint/20 border-igbanwo-mint" : ""}`}
        onClick={handleToggle}
      >
        <span className={selectedOption ? "text-gray-900" : "text-gray-500"}>
          {selectedOption ? formatOption(selectedOption) : placeholder}
        </span>
        <div className="flex items-center gap-2">
          {selectedOption && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-gray-100 rounded"
            >
              <X className="h-3 w-3" />
            </button>
          )}
          <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-igbanwo-mint/20 focus:border-igbanwo-mint"
                autoFocus
              />
            </div>
          </div>
          
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getValue(option)}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleSelect(option)}
                >
                  <span className="flex-1">{formatOption(option)}</span>
                  {getValue(option) === value && (
                    <Check className="h-4 w-4 text-igbanwo-mint" />
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500 text-center">
                {emptyMessage}
              </div>
            )}
          </div>
        </div>
      )}
      
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};